/**
 * /herramientas-gratuitas/:slug — a single free tool.
 *
 * Reads copy from `lib/tools.ts` and resolves the interactive widget from
 * `components/tools/registry.tsx`. Layout mirrors `FeaturePage` for visual
 * consistency. The server injects route-specific SEO into index.html for
 * crawlers; the useEffect here only covers client-side SPA navigation.
 */
import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, ChevronDown, Home, Sparkles } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { SiteFooter } from '../components/SiteFooter';
import { getToolBySlug, TOOLS } from '../lib/tools';
import { TOOL_WIDGETS } from '../components/tools/registry';

export function ToolPage() {
  const { slug } = useParams<{ slug: string }>();
  const tool = slug ? getToolBySlug(slug) : undefined;

  useEffect(() => {
    if (!tool) return;
    const prev = document.title;
    document.title = `${tool.title} (gratis) · Zettapost`;
    return () => {
      document.title = prev;
    };
  }, [tool]);

  if (!tool) return <Navigate to="/herramientas-gratuitas" replace />;

  const Widget = TOOL_WIDGETS[tool.slug];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />

      {/* ── Hero + widget ─────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-brand-50/40 to-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10 lg:py-14">
          <nav aria-label="Breadcrumbs" className="text-xs text-slate-500">
            <ol className="inline-flex items-center gap-1.5">
              <li>
                <Link to="/" className="inline-flex items-center gap-1 hover:text-slate-900 transition">
                  <Home size={11} />
                  Inicio
                </Link>
              </li>
              <li className="text-slate-300">/</li>
              <li>
                <Link to="/herramientas-gratuitas" className="hover:text-slate-900 transition">
                  Herramientas gratis
                </Link>
              </li>
              <li className="text-slate-300">/</li>
              <li className="text-slate-700 truncate max-w-[220px]">{tool.title}</li>
            </ol>
          </nav>

          <div className="mt-5 max-w-3xl">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-700">
              {tool.eyebrow} · Gratis
            </span>
            <h1 className="mt-3 text-3xl sm:text-4xl font-bold leading-tight text-slate-900">
              {tool.title}
            </h1>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">{tool.description}</p>
          </div>

          {/* Widget card */}
          <div className="mt-8 rounded-2xl bg-white border border-slate-200 shadow-sm p-5 sm:p-7">
            {Widget ? <Widget /> : <p className="text-slate-500">Herramienta no disponible.</p>}
          </div>
        </div>
      </section>

      {/* ── Intro (SEO copy) ──────────────────────────────────── */}
      <Section>
        <div className="max-w-3xl">
          <p className="text-slate-600 leading-relaxed text-lg">{tool.intro}</p>
        </div>
      </Section>

      {/* ── Cómo funciona ─────────────────────────────────────── */}
      <Section tone="muted">
        <Eyebrow>Cómo funciona</Eyebrow>
        <h2 className="mt-3 text-2xl lg:text-3xl font-bold text-slate-900">En 3 pasos</h2>
        <ol className="mt-8 grid gap-5 sm:grid-cols-3">
          {tool.howItWorks.map((step, i) => (
            <li key={i} className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-700 font-bold text-sm">
                {i + 1}
              </div>
              <h3 className="mt-3 font-semibold text-slate-900">{step.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{step.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* ── FAQ ───────────────────────────────────────────────── */}
      <Section>
        <div className="max-w-3xl">
          <Eyebrow>Preguntas frecuentes</Eyebrow>
          <h2 className="mt-3 text-2xl lg:text-3xl font-bold text-slate-900">
            Lo que la gente pregunta
          </h2>
          <div className="mt-8 space-y-3">
            {tool.faq.map((f, i) => (
              <details key={i} className="group rounded-xl bg-white border border-slate-200 overflow-hidden">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 px-5 py-4 font-semibold text-slate-900 hover:bg-slate-50 transition">
                  {f.q}
                  <ChevronDown size={16} className="text-slate-400 transition-transform group-open:rotate-180 shrink-0" />
                </summary>
                <div className="px-5 pb-4 text-slate-600 leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </Section>

      {/* ── Otras herramientas ────────────────────────────────── */}
      <Section tone="muted">
        <Eyebrow>Más herramientas gratis</Eyebrow>
        <h2 className="mt-3 text-2xl lg:text-3xl font-bold text-slate-900">Sigue explorando</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TOOLS.filter((t) => t.slug !== tool.slug).map((other) => {
            const Icon = other.icon;
            return (
              <Link
                key={other.slug}
                to={`/herramientas-gratuitas/${other.slug}`}
                className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-brand-300 hover:shadow-md transition"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 inline-flex items-center justify-center">
                    <Icon size={18} />
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">{other.eyebrow}</div>
                </div>
                <h3 className="mt-3 font-semibold text-slate-900 group-hover:text-brand-700 transition">{other.title}</h3>
                <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{other.shortDescription}</p>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* ── CTA final ─────────────────────────────────────────── */}
      <Section>
        <div className="rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 text-white p-8 lg:p-12 text-center">
          <Sparkles size={28} className="mx-auto opacity-90" />
          <h2 className="mt-3 text-2xl lg:text-3xl font-bold">Crea, programa y publica en todas tus redes</h2>
          <p className="mt-2 text-brand-100 max-w-xl mx-auto">
            Zettapost reúne IA, programación multi-red y link in bio en un solo sitio. 3 días gratis, cancela cuando quieras.
          </p>
          <Link
            to="/register"
            className="mt-6 inline-flex items-center gap-1.5 rounded-lg bg-white text-brand-700 font-semibold px-5 py-2.5 hover:bg-brand-50 transition"
          >
            Crear cuenta gratis
            <ArrowRight size={15} />
          </Link>
        </div>
      </Section>

      <SiteFooter />
    </div>
  );
}

function Section({ children, tone = 'plain' }: { children: React.ReactNode; tone?: 'plain' | 'muted' }) {
  return (
    <section className={tone === 'muted' ? 'bg-slate-50 border-y border-slate-200' : ''}>
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12 lg:py-16">{children}</div>
    </section>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-700">{children}</span>;
}
